/*
  <multicolumn-tabs> — GB tab controller for the Multicolumn section (Enable Tabs).
  New code (this file only). Reuses Dawn's slider-component: when a panel becomes
  visible its slider re-inits so slide widths, which are 0 while the panel is hidden,
  resolve correctly. ARIA tablist: roving tabindex, arrow / Home / End keys, click.
  No dependencies, deferred, self-registering.
*/
if (!customElements.get('multicolumn-tabs')) {
  customElements.define(
    'multicolumn-tabs',
    class MulticolumnTabs extends HTMLElement {
      constructor() {
        super();
        this.tablist = this.querySelector('[role="tablist"]');
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      }

      connectedCallback() {
        if (!this.tabs.length) return;
        this.revealTab(this.querySelector('[role="tab"][aria-selected="true"]'));
        this.tabs.forEach((tab, index) => {
          tab.addEventListener('click', (event) => {
            event.preventDefault();
            this.activate(index, true);
          });
          tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
        });
      }

      onKeydown(event, index) {
        const last = this.tabs.length - 1;
        let target = null;
        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            target = index === last ? 0 : index + 1;
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            target = index === 0 ? last : index - 1;
            break;
          case 'Home':
            target = 0;
            break;
          case 'End':
            target = last;
            break;
          default:
            return;
        }
        event.preventDefault();
        this.activate(target, true);
      }

      activate(index, focusTab) {
        this.tabs.forEach((tab, i) => {
          const selected = i === index;
          tab.setAttribute('aria-selected', selected ? 'true' : 'false');
          tab.setAttribute('tabindex', selected ? '0' : '-1');
          tab.classList.toggle('is-active', selected);
          if (selected && focusTab) tab.focus();
        });
        this.panels.forEach((panel, i) => {
          const selected = i === index;
          panel.toggleAttribute('hidden', !selected);
          if (selected) this.refreshSlider(panel);
        });
        this.revealTab(this.tabs[index]);
      }

      // Mobile: the track scrolls, so bring the newly active tab into view
      // without touching page scroll (scrollIntoView would move the document).
      revealTab(tab) {
        if (!this.tablist || !tab) return;
        const overflow = this.tablist.scrollWidth - this.tablist.clientWidth;
        if (overflow <= 0) return;
        const target = tab.offsetLeft - (this.tablist.clientWidth - tab.offsetWidth) / 2;
        const left = Math.max(0, Math.min(target, overflow));
        const motionOk = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.tablist.scrollTo({ left: left, behavior: motionOk ? 'smooth' : 'auto' });
      }

      // Re-init the panel's Dawn slider now that it has layout width.
      refreshSlider(panel) {
        const slider = panel.querySelector('slider-component');
        if (!slider) return;
        window.requestAnimationFrame(() => {
          if (typeof slider.initPages === 'function') slider.initPages();
          if (typeof slider.update === 'function') slider.update();
        });
      }
    }
  );
}
